/**
* ZipManager.js
* For loading, storing and retrieving zips
* See UnzipRequest for usage.
*
* Uses 
* - JSZip : https://stuk.github.io/jszip/
* - JSZipUtils :http://stuk.github.io/jszip-utils
* 
* 2020-01-27
* @author reinier@ranj.nl
**/

//For some reason, when we import the jszip code from the npm installed folder, ziploading doesnt work.
//The promise returned from the zipObject.async call in getFromZip never gets resolved or rejected.
//In a isolated test (without minifying) the code works properly.
//So probably something goes wrong while minifying with webpack. If we import the minified version all goes well..
//import JSZip from "jszip"; //this one fails silently
import JSZip from "jszip/dist/jszip.min";
import JSZipUtils from "jszip-utils/dist/jszip-utils.min";

export default class ZipManager {
    constructor(ziploadingFolder) {
        const lastChar = ziploadingFolder.substr(-1);
        if (lastChar != "\\" && lastChar != "/") ziploadingFolder += "/";
        this._ziploadingFolder = ziploadingFolder;
        this._promises = {};
    };

    getZip(id, progressHandler) {
        if (this._promises[id] == null) {
            //NB: we store the promise, not the zip itself!
            //This way, the zip is only loaded once, 
            //even if multiple UnzipRequest try to get the zip simultaneously/while it is loading.            
            const url = this._ziploadingFolder + id + ".zip";
            let zipPromise = JSZipUtils.getBinaryContent(url, { progress: progressHandler })
                .then(JSZip.loadAsync);
            this._promises[id] = zipPromise;
        }
        return this._promises[id];
    }

    removeZip(id) {
        delete this._promises[id];
    }

    removeAll() {
        this._promises = {};
    }

    getFromZip(bundleID, src, contentType) {
        return this.getZip(bundleID).then(zip => {
            //Normalize src: make sure path is separated by forward slashes and path has no leading ./
            src = src.split("\\").join("/");
            if (src.substr(0,2)=="./") {
                src = src.substr(2);
            }
            const zipObject = zip.file(src);
            return zipObject.async(contentType);
        });
    }

    removeFromZip(bundleID, src) {
        return this.getZip(bundleID).then(zip => {
            this._removeFromZip(zip, src, bundleID);
        });
    }

    /**
     * Remove data for src from zip, to reduce memory usage.
     * If no more files remain, zip itself can be removed from memory.
     **/
    _removeFromZip(zip, src, bundleID) {
        //remove item from zip
        zip.remove(src);
        //remove empty folder that may remain after item is removed
        const match = src.match(/(.*[\/\\])/);
        if (match == null) {
            //remove entry from zipLibrary if zip is empty        
            if (Object.keys(zip.files).length == 0) {
                this.removeZip(bundleID);
            }
            return;
        }
        const dirname = match[1];
        let notEmpty = false;
        for (let s in zip.files) {
            if (s != dirname && s.indexOf(dirname) == 0) {
                notEmpty = true;
                break;
            }
        }
        if (notEmpty) {
            return;
        }
        //recurse with folder (without trailing slash)
        this._removeFromZip(zip, dirname.substr(0, dirname.length - 1), bundleID);
    }
}


function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}